import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, BellRing, Download, Laptop, Link2, Send, Share, Smartphone, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, Badge, Card, CardContent, CardHeader, CardTitle } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import {
  canPromptInstall,
  currentSubscription,
  desktopBridge,
  deviceLabel,
  isInstalled,
  isIos,
  isWindows,
  nativeDeviceId,
  nativeShell,
  onInstallChange,
  promptInstall,
  pushSupported,
  registerApns,
  setNativeDeviceId,
  subscribePush,
} from '@/lib/app-install';
import { formatDateTime, formatRelative } from '@/lib/utils';

interface PushConfig {
  publicKey: string;
  apns: boolean;
  devices: { id: string; platform: 'web' | 'apns' | 'desktop'; device: string | null; createdAt: string; lastSuccessAt: string | null; endpointHash: string }[];
}
interface AppDownloads {
  windows: { version: string; size: number; uploadedAt: string } | null;
}

/** Versionsvergleich 1.2.3 */
const newer = (a: string, b: string) => {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) > (pb[i] ?? 0);
  return false;
};

/** Profil: Mitarbeiter-App installieren (Browser, Windows-Programm, iPhone/iPad) und Benachrichtigungen je Gerät einrichten. */
export function AppCard() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['push', 'config'], queryFn: () => api<PushConfig>('/push/config') });
  const { data: downloads } = useQuery({ queryKey: ['apps'], queryFn: () => api<AppDownloads>('/apps') });
  const [canInstall, setCanInstall] = useState(canPromptInstall());
  const [thisEndpoint, setThisEndpoint] = useState<string | null>(null);
  const [nativeId, setNativeId] = useState(nativeDeviceId);
  const shell = nativeShell();
  useEffect(() => onInstallChange(() => setCanInstall(canPromptInstall())), []);
  useEffect(() => {
    if (!shell) void currentSubscription().then((s) => setThisEndpoint(s?.endpoint ?? null));
  }, [data, shell]);
  useEffect(() => {
    const update = () => setNativeId(nativeDeviceId());
    window.addEventListener('sp-native-device', update);
    return () => window.removeEventListener('sp-native-device', update);
  }, []);
  const onError = (e: unknown) => toast.error(errorMessage(e), { duration: 10_000 });
  const refresh = () => qc.invalidateQueries({ queryKey: ['push', 'config'] });

  const enable = useMutation({
    mutationFn: async () => {
      if (shell === 'desktop') {
        const r = await api<{ id: string }>('/push/native', { method: 'POST', body: { platform: 'desktop', device: deviceLabel() } });
        setNativeDeviceId(r.id);
        return;
      }
      if (shell === 'ios') {
        const token = await registerApns();
        const r = await api<{ id: string }>('/push/native', { method: 'POST', body: { platform: 'apns', token, device: deviceLabel() } });
        setNativeDeviceId(r.id);
        return;
      }
      const sub = await subscribePush(data!.publicKey);
      await api('/push/subscriptions', { method: 'POST', body: { ...sub, device: deviceLabel() } });
      setThisEndpoint(sub.endpoint);
    },
    onSuccess: () => {
      toast.success('Benachrichtigungen auf diesem Gerät eingeschaltet');
      refresh();
    },
    onError,
  });
  const disable = useMutation({
    mutationFn: async () => {
      if (shell) {
        if (nativeId) await api('/push/subscriptions', { method: 'DELETE', body: { id: nativeId } });
        setNativeDeviceId(null);
        return;
      }
      const sub = await currentSubscription();
      if (sub) {
        await api('/push/subscriptions', { method: 'DELETE', body: { endpoint: sub.endpoint } });
        await sub.unsubscribe().catch(() => undefined);
      }
      setThisEndpoint(null);
    },
    onSuccess: () => {
      toast.success('Benachrichtigungen auf diesem Gerät ausgeschaltet');
      refresh();
    },
    onError,
  });
  const removeDevice = useMutation({
    mutationFn: async (id: string) => {
      await api('/push/subscriptions', { method: 'DELETE', body: { id } });
      if (id === nativeId) setNativeDeviceId(null);
    },
    onSuccess: refresh,
    onError,
  });
  const test = useMutation({
    mutationFn: () => api<{ sent: number; removed: number }>('/push/test', { method: 'POST' }),
    onSuccess: (r) => (r.sent ? toast.success(`Testbenachrichtigung an ${r.sent} Gerät${r.sent === 1 ? '' : 'e'} gesendet`) : toast.error('Kein Gerät erreichbar')),
    onError,
  });

  const installed = isInstalled();
  const ios = isIos();
  const supported = shell ? shell === 'desktop' || data?.apns !== false : pushSupported();
  const isThis = (d: PushConfig['devices'][number]) => (shell ? d.id === nativeId : d.platform === 'web' && Boolean(thisEndpoint?.endsWith(d.endpointHash)));
  const thisRegistered = Boolean(data?.devices.some(isThis));
  const win = downloads?.windows;
  const desktop = desktopBridge();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">App und Benachrichtigungen</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 text-sm">
        <div className="grid gap-2">
          <p className="font-medium">Als App installieren</p>
          {desktop ? (
            <>
              <p className="text-muted-foreground">Das Serviceportal läuft hier als Windows-Programm (Version {desktop.version}). Es bleibt nach dem Schließen im Infobereich der Taskleiste und meldet neue Vorgänge.</p>
              {win && newer(win.version, desktop.version) && (
                <Alert>
                  <span className="flex flex-wrap items-center gap-2">
                    Version {win.version} ist verfügbar.
                    <Button asChild variant="outline" size="sm">
                      <a href="/api/apps/windows">
                        <Download /> Herunterladen
                      </a>
                    </Button>
                  </span>
                </Alert>
              )}
              <div>
                <Button variant="ghost" size="sm" onClick={() => desktop.changeServer()}>
                  <Link2 /> Andere Portal-Adresse
                </Button>
              </div>
            </>
          ) : shell === 'ios' ? (
            <p className="text-muted-foreground">Das Serviceportal läuft hier als App.</p>
          ) : installed ? (
            <p className="text-muted-foreground">Das Serviceportal läuft auf diesem Gerät bereits als App.</p>
          ) : ios ? (
            <Alert>
              <span className="flex items-start gap-2">
                <Share className="mt-0.5 size-4 shrink-0" />
                <span>
                  Ohne App Store: In <strong>Safari</strong> auf <em>Teilen</em> tippen und <em>„Zum Home-Bildschirm“</em> wählen. Danach das Serviceportal vom Home-Bildschirm öffnen – erst dort lassen sich
                  Benachrichtigungen einschalten (ab iOS/iPadOS 16.4). Wurde Ihnen die Serviceportal-App von Ihrer Firma bereitgestellt (TestFlight/App Store), nutzen Sie diese.
                </span>
              </span>
            </Alert>
          ) : (
            <>
              {isWindows() && win && (
                <div className="grid gap-2 rounded-md border p-3">
                  <p>
                    <Laptop className="mr-1 inline size-4" /> <strong>Windows-Programm</strong> (Version {win.version}, {(win.size / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: win.size < 10 * 1024 * 1024 ? 1 : 0 })} MB): eigenes Fenster, Symbol im Infobereich, Start mit Windows,
                    Benachrichtigungen auch bei geschlossenem Fenster.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild size="sm">
                      <a href="/api/apps/windows">
                        <Download /> Windows-Programm herunterladen
                      </a>
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <a href={`serviceportal://connect?url=${encodeURIComponent(window.location.origin)}`}>
                        <Link2 /> Nach der Installation: mit diesem Portal verbinden
                      </a>
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Oder beim ersten Start die Adresse <code>{window.location.origin}</code> eingeben.
                  </p>
                </div>
              )}
              {canInstall ? (
                <div>
                  <Button variant="outline" size="sm" onClick={() => void promptInstall()}>
                    <Download /> Als Web-App installieren
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground">
                  {isWindows() && win ? 'Alternativ ohne Installation: ' : <Laptop className="mr-1 inline size-4" />}
                  In <strong>Edge</strong> oder <strong>Chrome</strong> über das Menü <em>„Apps → Diese Website als App installieren“</em> bzw. das Installationssymbol in der Adressleiste.
                </p>
              )}
            </>
          )}
        </div>

        <div className="grid gap-2">
          <p className="font-medium">Benachrichtigungen auf diesem Gerät</p>
          <p className="text-muted-foreground">Zu allem, worüber Sie das Portal per E-Mail informiert (z. B. zugewiesene Tickets, Kundenantworten), kommt zusätzlich eine Benachrichtigung. Ein Klick darauf öffnet den Vorgang.</p>
          {shell === 'ios' && data && !data.apns ? (
            <Alert>Apple-Push ist auf dem Server noch nicht eingerichtet (Plattformverwaltung → Apps).</Alert>
          ) : !supported ? (
            <Alert>{ios && !installed ? 'Auf iPhone/iPad erst als App installieren (siehe oben).' : 'Dieser Browser unterstützt keine Push-Benachrichtigungen.'}</Alert>
          ) : (
            <div className="flex flex-wrap gap-2">
              {thisRegistered ? (
                <Button variant="outline" size="sm" onClick={() => disable.mutate()} loading={disable.isPending}>
                  <BellOff /> Auf diesem Gerät ausschalten
                </Button>
              ) : (
                <Button size="sm" onClick={() => enable.mutate()} loading={enable.isPending} disabled={!data}>
                  <BellRing /> Auf diesem Gerät einschalten
                </Button>
              )}
              {(data?.devices.length ?? 0) > 0 && (
                <Button variant="outline" size="sm" onClick={() => test.mutate()} loading={test.isPending}>
                  <Send /> Testbenachrichtigung
                </Button>
              )}
            </div>
          )}
        </div>

        {(data?.devices.length ?? 0) > 0 && (
          <div className="grid gap-1">
            <p className="text-xs font-medium text-muted-foreground">Eingerichtete Geräte</p>
            {data!.devices.map((d) => (
              <div key={d.id} className="flex items-center gap-2 rounded-md border px-3 py-2">
                {d.platform === 'desktop' ? <Laptop className="size-4 shrink-0 text-muted-foreground" /> : <Smartphone className="size-4 shrink-0 text-muted-foreground" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">
                    {d.device ?? 'Gerät'} {isThis(d) && <Badge variant="outline">dieses Gerät</Badge>}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    seit {formatDateTime(d.createdAt)}
                    {d.lastSuccessAt && ` · zuletzt zugestellt ${formatRelative(d.lastSuccessAt)}`}
                  </span>
                </span>
                <Button variant="ghost" size="icon" className="size-8" aria-label={`${d.device ?? 'Gerät'} entfernen`} onClick={() => removeDevice.mutate(d.id)}>
                  <Trash2 />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
